import type { IncidentStatus, ServiceStatus } from "./types";

/** Wording and tone for each state. Shared by both pages so a status can never
 *  read one way on the summary and another on the incident page. */

export const SERVICE_LABEL: Record<ServiceStatus, string> = {
  operational: "Operational",
  degraded: "Degraded",
  partial_outage: "Partial outage",
  major_outage: "Outage",
  maintenance: "Maintenance",
  unknown: "Unknown",
};

export const OVERALL_LABEL: Record<ServiceStatus, string> = {
  operational: "All services are running normally",
  degraded: "Some services are slower than usual",
  partial_outage: "Some services are unavailable",
  major_outage: "A service is unavailable",
  maintenance: "Planned maintenance in progress",
  unknown: "Service status is unknown",
};

/**
 * `unknown` is neutral, not green and not red. It means "we have not heard from
 * the monitor" — dressing that up as healthy would be a lie, and colouring it
 * as an outage would be a different one.
 */
export const TONE: Record<ServiceStatus, string> = {
  operational: "tone-ok",
  maintenance: "tone-warn",
  degraded: "tone-warn",
  partial_outage: "tone-bad",
  major_outage: "tone-bad",
  unknown: "tone-unknown",
};

export const INCIDENT_LABEL: Record<IncidentStatus, string> = {
  investigating: "Investigating",
  identified: "Identified",
  monitoring: "Monitoring",
  resolved: "Resolved",
};

/** Date/time in the resident-facing format used across the app (en-GB). */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "99.98% uptime over 30 days", or a plain statement when nothing is known —
 *  never a fabricated 100%. */
export function uptimeLine(uptime30d: number | null, lastSynced: string | null): string {
  if (uptime30d !== null && uptime30d !== undefined) {
    return `${uptime30d}% uptime over 30 days`;
  }
  return lastSynced ? "Uptime history is being collected" : "No monitoring data yet";
}
